// These functions are are used on the frontend to fetch content, and also by github pages to create a snapshot periodically.
//TODO: make it and ENV variable
export const serverBase = 'https://backend.xiasbio.com/wp-json/wp/v2';
/* export const serverBase = 'http://localhost/wp-json/wp/v2'; */
/* export const serverBase =
  'https://test.extrarelish.co.uk/xiasbio/wp-json/wp/v2'; */
export const server = `${serverBase}/pages/?slug=`;
export const media = `${serverBase}/media/`;
export const post = `${serverBase}/submit-data/`;
export const blog = `${serverBase}/posts?_fields=id,date,slug,title.rendered,featured_media`;
export const blogSingle = `${serverBase}/posts/?slug=`;
export const errorPage = 'error';
export const blogListKey = 'blogs';
// NOTE: Mirrored section from globalUtils.ts, so this file doesn't have imports in Node.js
const homePath = 'home';
var API;
(function (API) {
    API["setup"] = "api-setup";
    API["products"] = "api-products";
    API["team"] = "api-team";
    API["news"] = "api-news";
    API["events"] = "api-events";
    API["blog"] = "blog-template";
})(API || (API = {}));
const getPage = (urlPath) => urlPath?.split('/').filter(Boolean).pop() ?? '';
const getPath = (path) => getPage(path) || homePath;
// END of mirrored section from globalUtils.ts
/* ------------------------------------------------------------------ *
 * Content pruning
 *
 * ACF flexible content returns every field of every layout on every
 * section, so ~95% of what the CMS sends is empty padding that nothing
 * reads. Pruning here — rather than in buildSnapshot.ts — means the
 * static snapshot and the live fetch produce identically shaped data,
 * so a page can never behave differently depending on which one served
 * it.
 *
 * Verified against the consuming code before adding anything below:
 * every key here has no reader in src/, and every section keeps the
 * sub-object matching its own `type`.
 * ------------------------------------------------------------------ */
// Keys with no reader anywhere in src/. `acf_fc_layout` is only ever
// *excluded* (globalUtils countTruthyKeys/renderSection, seo_filter), and
// section dispatch keys off `type`, so its value is never read.
const DEAD_KEYS = new Set([
    '', // stray empty-string key ACF emits inside `team`
    'acf_fc_layout',
    'button_text',
    'gradient',
    'menu_blocks',
    'proximity_snap',
    'scale_direction',
    'scale_mobile',
    'story_lines_mobile',
    'transition_position_mobile',
    'use_flexible_background',
]);
/**
 * An "empty" value in ACF terms: the CMS sends `""` for every unset text
 * field, `false` for every unset checkbox and `null` for every unset
 * relation, on every layout of every section.
 *
 * `0` is deliberately not empty -- depths, scales, percentages and the
 * `position` numbers are all legitimately zero.
 */
const isEmptyValue = (value) => value === null || value === undefined || value === '' || value === false;
// A branch is empty when nothing inside it survives: an empty scalar, an
// array of empties, or an object whose every value is empty.
const isEmptyBranch = (value) => {
    if (isEmptyValue(value))
        return true;
    if (Array.isArray(value))
        return value.every(isEmptyBranch);
    if (typeof value === 'object')
        return Object.values(value).every(isEmptyBranch);
    return false;
};
const stripDeadKeys = (node) => {
    if (Array.isArray(node))
        return node.map(stripDeadKeys);
    if (node && typeof node === 'object') {
        const out = {};
        for (const [key, value] of Object.entries(node)) {
            if (DEAD_KEYS.has(key))
                continue;
            out[key] = stripDeadKeys(value);
        }
        return out;
    }
    return node;
};
/**
 * Drops every key whose value carries no information.
 *
 * Two rules keep this safe for the consuming code:
 *
 *  - array *elements* are pruned but never removed. Sections, cards and
 *    parallax elements are addressed by index (`page_sections[activeSection]`),
 *    so closing a gap would silently repoint a page at the wrong entry. An
 *    element with nothing left in it stays as `{}`.
 *  - a reader has to treat a missing key exactly as it treated `""`/`false`.
 *    Both are falsy, so `if (x)`, `{x}` in JSX and `x ?? fallback` are
 *    unaffected; only an explicit `=== ''`, `=== false`, `in` or `Object.keys`
 *    test would notice the difference.
 */
const pruneEmpty = (node) => {
    if (Array.isArray(node))
        return node.map(pruneEmpty);
    if (node && typeof node === 'object') {
        const out = {};
        for (const [key, value] of Object.entries(node)) {
            const pruned = pruneEmpty(value);
            if (isEmptyBranch(pruned))
                continue;
            out[key] = pruned;
        }
        return out;
    }
    return node;
};
/**
 * Normalises one page's ACF payload. Safe to call on any shape — pages,
 * api-* endpoints and blog posts alike.
 */
export const pruneContent = (data) => pruneEmpty(stripDeadKeys(data));
export const fetchContent = async (callback, setFirstFetch) => {
    const setup = await fetchPath(API.setup, callback);
    setFirstFetch?.(true);
    // collect promises from sub-fetches and await them
    const tasks = [];
    setup?.components?.forEach((comp) => {
        const { slug } = comp;
        tasks.push(fetchPath(slug, callback));
    });
    setup?.menu?.forEach((comp) => {
        tasks.push(fetchPath(comp.slug, callback));
    });
    setup?.footer_2?.forEach((comp) => {
        tasks.push(fetchPath(comp.slug, callback));
    });
    // Wait for *all* subfetches (don’t fail the whole build if one rejects)
    await Promise.allSettled(tasks); // NEW
};
export const fetchPath = async (p, callback, isProductPage, isBlogPage, isBlogList) => {
    const path = getPath(p);
    const rawData = await fetchACFContent(path, isBlogPage, isBlogList);
    //TODO: show errorPage;
    if (rawData === errorPage)
        return null;
    // rawData is an array for WP pages/posts with ?slug=
    const data = rawData?.[0]?.acf;
    const rawWithBlogs = data
        ? data
        : isBlogList
            ? { [blogListKey]: rawData }
            : isBlogPage
                ? { blog: rawData?.[0] }
                : undefined;
    // Prune before the callback so the store and the static snapshot builder
    // both receive the same shape.
    const dataWithBlogs = rawWithBlogs
        ? pruneContent(rawWithBlogs)
        : rawWithBlogs;
    callback(path, dataWithBlogs, isProductPage, isBlogPage);
    // If the products index page includes product entries, fan out to product pages
    if (path === API.products && data?.products?.length) {
        await fetchProductPages(data.products, callback);
    }
    return dataWithBlogs;
};
const fetchProductPages = async (data, callback) => {
    // return a promise for all page fetches
    await Promise.allSettled(data.map((d) => fetchPath(d.slug, callback, true)));
};
// Keep return type as Promise<any>; caller treats 'errorPage' as a soft failure.
export const fetchACFContent = (page, isBlogPage, isBlogList) => new Promise(async (resolve) => {
    try {
        const url = isBlogList
            ? blog
            : `${isBlogPage ? blogSingle : server}${page}`;
        const response = await fetch(url);
        if (!response.ok)
            throw new Error(`HTTP error! Status: ${response.status}`);
        const data = await response.json(); // pages/posts?slug= → array
        resolve(data);
    }
    catch {
        resolve(errorPage);
    }
});
